import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { OnboardingStateService } from '../onboarding/onboarding-state.service';

const DASHBOARD_ROUTE = '/workspaces/dashboard';

/**
 * Stops users from bypassing mandatory onboarding steps (signup-details, intro-survey,
 * pricing, workspace/project creation, ...) by editing the URL directly.
 *
 * Attached both to the onboarding routes themselves (so an out-of-order step redirects
 * back to the pending one) and to the dashboard routes (so an incomplete onboarding
 * can never be skipped by deep-linking straight to /projects or /workspaces).
 */
@Injectable({
    providedIn: 'root',
})
export class OnboardingGuard {
    constructor(private router: Router, private onboardingState: OnboardingStateService) { }

    canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        return this.check(state.url);
    }

    canActivateChild(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        return this.check(state.url);
    }

    private check(rawUrl: string): boolean {
        const url = rawUrl.split('?')[0].split('#')[0];

        // Forgot-password is a separate flow from onboarding — it reuses /account/verify-otp
        // (set apart by sessionStorage.isForgotPassword, written by forget-password.component.ts)
        // before landing on /account/reset-password. Neither should ever be subject to onboarding
        // step enforcement: not the "already has an account" dashboard redirect (defeats the whole
        // point of resetting a password), and not a leftover pending signup step from this browser
        // hijacking the reset flow either.
        if (
            sessionStorage.getItem('isForgotPassword') === 'true' &&
            (url === '/account/verify-otp' || url === '/account/reset-password')
        ) {
            return true;
        }

        const step = this.onboardingState.getStep();
        const pending = !!step && step !== 'completed';

        if (pending) {
            // From the OTP-verification step specifically, let the user bail out instead of being
            // trapped here forever if the code never arrives: back to signup to restart with a
            // different email (the verify-otp back-arrow), or give up and sign in to an existing
            // account instead (also covers reopening the app later and heading straight to signin).
            // No other pending step gets this exemption.
            if (step === 'verify-otp' && (url === '/account/signup' || url === '/account/signin')) {
                return true;
            }

            const requiredRoute = this.onboardingState.getRouteForStep(step);
            if (requiredRoute && url !== requiredRoute) {
                this.router.navigateByUrl(requiredRoute);
                return false;
            }
            return true;
        }

        // No pending step tracked: onboarding was either never started in this browser, or is
        // already complete. Both cases are only relevant on the onboarding routes themselves —
        // dashboard access is already fully covered by the `pending` branch above.
        if (this.onboardingState.isOnboardingRoutePath(url)) {
            const hasAccount = localStorage.getItem('currentUser') !== null;

            if (!hasAccount) {
                // Nothing tracked and no account at all — nobody has started onboarding in this
                // browser, so don't allow deep-linking straight into a step (e.g. /get-started/create-workspace).
                this.router.navigateByUrl('/account/signup');
                return false;
            }

            if (this.onboardingState.isPreAuthRoutePath(url)) {
                // Already has an account — no reason to redo signup-details/verify-otp.
                this.router.navigateByUrl(DASHBOARD_ROUTE);
                return false;
            }

            // Has an account and is hitting a post-auth onboarding step (intro-survey, pricing, ...)
            // with nothing pending tracked. This can legitimately happen for a session that was
            // mid-onboarding before step-tracking shipped — allow it rather than risk granting
            // dashboard access to an account that hasn't actually finished onboarding.
            return true;
        }

        return true;
    }
}
